<template>
  <div class="col-md-8 box-col-30 xl-30">
    <div class="card product">
      <div class="card-header pb-0">
        <div class="d-flex justify-content-between">
          <div class="flex-grow-1">
            <p class="square-after f-w-600">Productos Destacados<i class="fa fa-circle"> </i></p>
            <h4>Productos</h4>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Cargando...</span>
          </div>
        </div>
        <div v-else-if="error" class="text-center text-muted py-4">
          <p>{{ error }}</p>
        </div>
        <div v-else-if="!products.length" class="text-center text-muted py-4">
          <p>No hay datos disponibles</p>
        </div>
        <div v-else class="table-responsive theme-scrollbar">
          <table class="table">
            <tbody>
              <tr v-for="product in products" :key="product.CODIGO || product.objectID">
                <td>
                  <div class="d-flex align-items-center">
                    <img
                      class="img-fluid circle"
                      :src="getProductImage(product)"
                      :alt="product.DESCRIPCION || 'Producto'"
                      style="width: 50px; height: 50px; object-fit: cover; border-radius: 50%"
                      @error="handleImageError"
                    />
                    <div class="flex-grow-1">
                      <span>
                        {{ product.DESCRIPCION || 'Sin descripción' }}
                      </span>
                      <p class="mb-0">
                        {{ formatQuantity(product) }} disponible{{
                          formatQuantity(product) !== 1 ? 's' : ''
                        }}
                      </p>
                    </div>
                    <div class="active-status active-online"></div>
                  </div>
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span :class="{ 'text-primary': isOnSale(product) }">
                      {{ formatPrice(product) }}
                    </span>
                    <span
                      v-if="isOnSale(product)"
                      class="text-muted text-decoration-line-through small"
                    >
                      {{ formatRegularPrice(product) }}
                    </span>
                    <span
                      v-if="getOfferType(product)"
                      class="badge mt-1"
                      :class="getOfferTypeBadgeClass(product)"
                      style="font-size: 0.65rem; padding: 3px 8px; display: inline-block"
                    >
                      {{ getOfferType(product) }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { searchProducts } from '@/services/productsSearchService'

// Cache configuration
const CACHE_KEY = 'dashboard_popular_products'
const CACHE_DURATION = 60 * 60 * 1000 // 1 hora en milisegundos

export default {
  name: 'SellingProduct',
  data() {
    return {
      products: [],
      loading: false,
      error: null,
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),

    /**
     * Get the store code to use for prices and inventory
     * If defaultStore is 0 or null, use 7127, otherwise use defaultStore
     */
    storeCode() {
      if (!this.currentUser || !this.currentUser.defaultStore) {
        return '7127'
      }

      const defaultStore = this.currentUser.defaultStore
      const storeNum = Number(defaultStore)

      // If defaultStore is 0, '0', null, undefined, empty string, or NaN, use 7127
      if (defaultStore === 0 || defaultStore === '0' || storeNum === 0 || isNaN(storeNum)) {
        return '7127'
      }

      return String(defaultStore)
    },
  },
  watch: {
    // Watch for user changes to reload products if store changes
    currentUser: {
      handler(newUser, oldUser) {
        if (newUser && oldUser && newUser.defaultStore !== oldUser.defaultStore) {
          // Clear cache and reload if store changed
          localStorage.removeItem(CACHE_KEY)
          this.loadProducts()
        }
      },
      deep: true,
    },
  },
  async mounted() {
    await this.loadProducts()
  },
  methods: {
    /**
     * Load products from API with cache
     */
    async loadProducts() {
      // Check cache first
      const cached = this.getCachedProducts()
      if (cached) {
        this.products = cached
        return
      }

      this.loading = true
      this.error = null

      try {
        const result = await searchProducts({
          q: '',
          page: 0,
          perPage: 5,
        })

        if (result.success && result.results && Array.isArray(result.results)) {
          this.products = result.results.slice(0, 5)
          // Save to cache
          this.setCachedProducts(this.products)
        } else {
          this.error = result.error || 'Error al cargar productos'
          this.products = []
        }
      } catch (error) {
        console.error('Error loading popular products:', error)
        this.error = 'Error al cargar productos'
        this.products = []
      } finally {
        this.loading = false
      }
    },

    /**
     * Get cached products if still valid
     */
    getCachedProducts() {
      try {
        const cached = localStorage.getItem(CACHE_KEY)
        if (!cached) return null

        const { data, timestamp } = JSON.parse(cached)
        const now = Date.now()

        // Check if cache is still valid (less than 1 hour old)
        if (now - timestamp < CACHE_DURATION) {
          return data
        }

        // Cache expired, remove it
        localStorage.removeItem(CACHE_KEY)
        return null
      } catch (error) {
        console.error('Error reading cache:', error)
        return null
      }
    },

    /**
     * Save products to cache
     */
    setCachedProducts(products) {
      try {
        const cacheData = {
          data: products,
          timestamp: Date.now(),
        }
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData))
      } catch (error) {
        console.error('Error saving cache:', error)
      }
    },

    /**
     * Get product image URL
     * Prioridad: URLS512 > URLS256 > URLS128
     */
    getProductImage(product) {
      if (!product) {
        return ''
      }

      // Fallback to URLS128
      if (product.URLS128 && product.URLS128.trim()) {
        return product.URLS128
      }

      // Return empty string if no image found
      return ''
    },

    /**
     * Format quantity (EXISTENCIA)
     */
    formatQuantity(product) {
      if (!product) return 0
      const key = `EXISTENCIA_${this.storeCode}`
      const existencia = this.getStoreValue(product, key, 0)
      return Number(existencia) || 0
    },

    /**
     * Format price with currency (shows offer price if on sale, regular price otherwise)
     */
    formatPrice(product) {
      if (!product) return '$0.00'
      let price = 0

      if (this.isOnSale(product)) {
        // Use offer price if on sale
        price = this.getStoreValue(product, `PRECIOOFERTA_${this.storeCode}`, 0)
      } else {
        // Use regular price
        price = this.getStoreValue(product, `PRECIO_${this.storeCode}`, 0)
      }

      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(Number(price) || 0)
    },

    /**
     * Format regular price (always shows the regular price, even if on sale)
     */
    formatRegularPrice(product) {
      if (!product) return '$0.00'
      const price = this.getStoreValue(product, `PRECIO_${this.storeCode}`, 0)

      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(Number(price) || 0)
    },

    /**
     * Get offer type label
     * 101 = "Descuento directo"
     * 103 = "Abono a tarjeta club"
     */
    getOfferType(product) {
      if (!this.isOnSale(product)) {
        return null
      }

      const tipoOferta = this.getStoreValue(product, `TIPOOFERTA_${this.storeCode}`, 0)

      if (tipoOferta === 101) {
        return 'Descuento directo'
      } else if (tipoOferta === 103) {
        return 'Abono a tarjeta club'
      }

      return null
    },

    /**
     * Get badge class for offer type
     */
    getOfferTypeBadgeClass(product) {
      const tipoOferta = this.getStoreValue(product, `TIPOOFERTA_${this.storeCode}`, 0)

      if (tipoOferta === 101) {
        return 'badge-primary'
      } else if (tipoOferta === 103) {
        return 'badge-info'
      }

      return 'badge-secondary'
    },

    /**
     * Check if product is on sale
     * Conditions:
     * 1. OFERTA_$storeCode = 1
     * 2. Current timestamp is between INICIOOFERTA and FINOFERTA
     * 3. PRECIOOFERTA < PRECIO
     */
    isOnSale(product) {
      const storeCode = this.storeCode
      const oferta = this.getStoreValue(product, `OFERTA_${storeCode}`, 0)

      if (oferta !== 1) {
        return false
      }

      const precioOferta = this.getStoreValue(product, `PRECIOOFERTA_${storeCode}`, 0)
      const precio = this.getStoreValue(product, `PRECIO_${storeCode}`, 0)

      // Check if offer price is less than regular price
      if (precioOferta >= precio || precioOferta <= 0) {
        return false
      }

      // Check if current time is within offer period
      const inicioOferta = this.getStoreValue(product, `INICIOOFERTA_${storeCode}`, 0)
      const finOferta = this.getStoreValue(product, `FINOFERTA_${storeCode}`, 0)

      if (!inicioOferta || !finOferta) {
        return false
      }

      const now = Date.now()
      return now >= inicioOferta && now <= finOferta
    },

    /**
     * Get store-specific value from product
     */
    getStoreValue(product, key, defaultValue = 0) {
      if (!product || !key) return defaultValue

      let value = product[key]

      // If value is undefined or null, return default
      if (value === undefined || value === null) {
        return defaultValue
      }

      // Convert numeric strings to numbers
      if (typeof value === 'string' && value.trim() !== '' && !isNaN(value)) {
        value = Number(value)
      }

      return value
    },

    /**
     * Handle image loading errors
     * Try fallback image sizes if main image fails
     */
    handleImageError(event) {
      const img = event.target
      const currentSrc = img.src

      // Try different image sizes as fallback
      if (currentSrc.includes('URLS512') || currentSrc.includes('512')) {
        // If URLS512 failed, try URLS256
        const newSrc = currentSrc.replace('512', '256')
        if (newSrc !== currentSrc) {
          img.src = newSrc
          return
        }
      }

      if (currentSrc.includes('URLS256') || currentSrc.includes('256')) {
        // If URLS256 failed, try URLS128
        const newSrc = currentSrc.replace('256', '128')
        if (newSrc !== currentSrc) {
          img.src = newSrc
          return
        }
      }

      // If all attempts failed, hide the image or show a broken image icon
      img.style.display = 'none'
    },
  },
}
</script>
